@let current = displayedContext();

<button
  #triggerButton
  type="button"
  class="fb-switcher__trigger"
  [class.fb-switcher__trigger--open]="isOpen()"
  [class.fb-switcher__trigger--switching]="isSwitching()"
  [attr.aria-expanded]="isOpen()"
  [attr.aria-busy]="isSwitching()"
  [disabled]="isSwitching()"
  aria-haspopup="dialog"
  cdkOverlayOrigin
  #switcherTrigger="cdkOverlayOrigin"
  data-testid="btn-context-switcher"
  (click)="toggle()">
  @if (current?.kind === 'topLevelDashboard') {
    <span class="fb-switcher__avatar">
      <mat-icon aria-hidden="true">grid_view</mat-icon>
    </span>
    <span class="fb-switcher__text">
      <span class="fb-switcher__name">{{ 'SHARED.SYSTEM.TOP_LEVEL_DASHBOARD' | translate }}</span>
      <span class="fb-switcher__subtitle">{{
        'SHARED.SYSTEM.TOP_LEVEL_DASHBOARD_SUBTITLE' | translate
      }}</span>
    </span>
  } @else {
    <app-default-avatar [name]="current?.name" [size]="38" />
    <span class="fb-switcher__text">
      <span class="fb-switcher__name" [appTooltipWhenTruncated]="current?.name || ''">{{
        current?.name
      }}</span>
      @if (current) {
        <span class="fb-switcher__subtitle">
          {{
            current.kind === 'venue' ? accountChildrenName() : ('SHARED.SYSTEM.ACCOUNT' | translate)
          }}
          @if (current.subtitle) {
            <span class="fb-switcher__subtitle-detail">· {{ current.subtitle }}</span>
          }
        </span>
      }
    </span>
  }
  @if (isSwitching()) {
    <mat-progress-spinner
      class="fb-switcher__spinner"
      mode="indeterminate"
      [diameter]="20"
      [strokeWidth]="2" />
  } @else {
    <mat-icon class="fb-switcher__chevron" aria-hidden="true">
      {{ isOpen() ? 'expand_less' : 'expand_more' }}
    </mat-icon>
  }
</button>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="switcherTrigger"
  [cdkConnectedOverlayOpen]="isOpen()"
  [cdkConnectedOverlayPositions]="dropdownPositions"
  [cdkConnectedOverlayViewportMargin]="12"
  [cdkConnectedOverlayPush]="true"
  (overlayOutsideClick)="close()">
  <div
    class="fb-popout fb-switcher__panel"
    role="dialog"
    aria-modal="false"
    [attr.aria-label]="'SHARED.SYSTEM.SWITCH_CONTEXT' | translate"
    animate.enter="fb-switcher__panel--enter"
    animate.leave="fb-switcher__panel--leave"
    data-testid="popout-context-switcher">
    @let drilled = drilledAccount();

    @if (drilled) {
      <div class="fb-switcher__drill-head">
        <button
          type="button"
          class="fb-switcher__back"
          [attr.aria-label]="'SHARED.SYSTEM.BACK' | translate"
          data-testid="btn-context-switcher-back"
          (click)="leaveDrilldown()">
          <mat-icon aria-hidden="true">arrow_back</mat-icon>
        </button>
        <app-default-avatar [name]="drilled.name" [size]="38" />
        <span class="fb-switcher__drill-text">
          <span class="fb-switcher__drill-overline">{{ 'SHARED.SYSTEM.UNITS' | translate }}</span>
          <span class="fb-switcher__drill-name" [appTooltipWhenTruncated]="drilled.name">{{
            drilled.name
          }}</span>
        </span>
      </div>
    } @else {
      <p class="fb-switcher__title">{{ 'SHARED.SYSTEM.WHERE_TO_NEXT' | translate }}</p>
    }

    <!-- One placeholder for both levels: the search reaches everything from either of them. -->
    <div class="fb-switcher__search">
      <mat-icon aria-hidden="true">search</mat-icon>
      <input
        #searchInput
        type="text"
        autocomplete="off"
        [value]="query()"
        [attr.placeholder]="'SHARED.SYSTEM.SEARCH_ACCOUNTS_AND_VENUES' | translate"
        [attr.aria-label]="'SHARED.SYSTEM.SEARCH_ACCOUNTS_AND_VENUES' | translate"
        data-testid="input-context-switcher-search"
        (input)="onQueryInput($event)"
        (keydown)="onSearchKeydown($event)" />
    </div>

    <div class="fb-switcher__body" #panelBody (keydown)="onBodyKeydown($event)">
      @if (hasNoResults()) {
        <p class="fb-switcher__empty">{{ 'SHARED.SYSTEM.NO_RESULTS' | translate }}</p>
      } @else {
        @let accounts = visibleAccounts();

        @if (!drilled && !isSearching() && isTopLevelDashboardAllowed()) {
          <button
            type="button"
            class="fb-switcher__row"
            [class.fb-switcher__row--current]="isCurrentTopLevelDashboard()"
            data-testid="btn-context-switcher-top-level-dashboard"
            (click)="selectTopLevelDashboard()">
            <span class="fb-switcher__avatar">
              <mat-icon aria-hidden="true">grid_view</mat-icon>
            </span>
            <span class="fb-switcher__row-text">
              <span class="fb-switcher__row-name">{{
                'SHARED.SYSTEM.TOP_LEVEL_DASHBOARD' | translate
              }}</span>
              <span class="fb-switcher__row-subtitle">{{
                'SHARED.SYSTEM.TOP_LEVEL_DASHBOARD_SUBTITLE' | translate
              }}</span>
            </span>
            @if (isCurrentTopLevelDashboard()) {
              <mat-icon class="fb-switcher__check" aria-hidden="true">check</mat-icon>
            }
          </button>

          <span class="fb-switcher__spacer" aria-hidden="true"></span>
        }

        @if (!drilled) {
          @if (isSearching() && accounts.length > 0) {
            <p class="fb-switcher__group-label">{{ 'SHARED.SYSTEM.ACCOUNTS' | translate }}</p>
          }

          <ng-container *ngTemplateOutlet="accountRows; context: { $implicit: accounts }" />
        }

        <!--
          Inside an account's venues, the account itself is one tap away. Coming from a venue the
          panel opens here, so this is the way back up to account level without going through the
          account list. The head above already names the account, so the row carries the action
          rather than repeating it. A search puts it away unless it is what the query asks for —
          the group below offers every account except this one.
        -->
        @if (drilled && drilled.canUse && (!isSearching() || queryNamesDrilledAccount())) {
          <button
            type="button"
            class="fb-switcher__row"
            [class.fb-switcher__row--current]="isCurrentAccount(drilled)"
            [disabled]="isCurrentAccount(drilled)"
            [attr.aria-current]="isCurrentAccount(drilled) ? 'true' : null"
            data-testid="btn-context-switcher-account-level"
            (click)="selectAccount(drilled)">
            <span class="fb-switcher__avatar">
              <mat-icon aria-hidden="true">corporate_fare</mat-icon>
            </span>
            <span class="fb-switcher__row-text">
              <span class="fb-switcher__row-name">{{ accountRowLabelKey() | translate }}</span>
            </span>
            @if (isCurrentAccount(drilled)) {
              <mat-icon class="fb-switcher__check" aria-hidden="true">check</mat-icon>
            }
          </button>

          <mat-divider class="fb-switcher__divider" />
        }

        @if (venuesLabelKey(); as venuesLabelKey) {
          <p class="fb-switcher__group-label">{{ venuesLabelKey | translate }}</p>
        }

        @if (isLoadingVenues()) {
          <ng-container
            *ngTemplateOutlet="loadingRows; context: { $implicit: 'context-switcher-loading' }" />
        }

        @let venues = visibleVenues();

        <ng-container *ngTemplateOutlet="venueRows; context: { $implicit: venues }" />

        <!--
          Everything the query matches outside the account being browsed. Without it a search
          aimed at another account inside a drilled one is a dead end, and getting there means
          stepping back out to the account list first. Accounts and venues are labelled apart:
          one query routinely matches several of each, and the rows alone do not say which is
          which.
        -->
        @if (showsOtherAccountsGroup()) {
          @let otherVenues = otherAccountVenues();

          @if (accounts.length > 0) {
            <p class="fb-switcher__group-label">{{ 'SHARED.SYSTEM.OTHER_ACCOUNTS' | translate }}</p>

            <ng-container *ngTemplateOutlet="accountRows; context: { $implicit: accounts }" />
          }

          @if (isLoadingOtherAccounts()) {
            <ng-container
              *ngTemplateOutlet="
                loadingRows;
                context: { $implicit: 'context-switcher-other-accounts-loading' }
              " />
          }

          @if (otherVenues.length > 0) {
            <p class="fb-switcher__group-label">
              {{ 'SHARED.SYSTEM.VENUES_IN_OTHER_ACCOUNTS' | translate }}
            </p>

            <ng-container *ngTemplateOutlet="venueRows; context: { $implicit: otherVenues }" />
          }
        }
      }
    </div>
  </div>
</ng-template>

<ng-template #accountRows let-accounts>
  @for (account of accounts; track account.uuid) {
    <div class="fb-switcher__account">
      <button
        type="button"
        class="fb-switcher__row"
        [class.fb-switcher__row--current]="isCurrentAccount(account)"
        [attr.data-testid]="'btn-context-switcher-account-' + account.uuid"
        (click)="selectAccount(account)">
        <app-default-avatar [name]="account.name" [size]="38" />
        <span class="fb-switcher__row-text">
          <span class="fb-switcher__row-name" [appTooltipWhenTruncated]="account.name">
            <app-highlighted-text [parts]="account.name | highlight: normalizedQuery()" />
          </span>
          @if (account.type) {
            <span class="fb-switcher__row-subtitle" [appTooltipWhenTruncated]="account.type">{{
              account.type
            }}</span>
          }
        </span>
        @if (isCurrentAccount(account)) {
          <mat-icon class="fb-switcher__check" aria-hidden="true">check</mat-icon>
        }
      </button>

      <button
        type="button"
        class="fb-switcher__drill"
        [attr.aria-label]="'SHARED.SYSTEM.UNITS' | translate"
        [attr.data-testid]="'btn-context-switcher-drill-' + account.uuid"
        (click)="drillInto(account, $event)">
        <mat-icon aria-hidden="true">chevron_right</mat-icon>
      </button>
    </div>
  }
</ng-template>

<ng-template #venueRows let-venues>
  @for (venue of venues; track venue.uuid) {
    <button
      type="button"
      class="fb-switcher__row"
      [class.fb-switcher__row--current]="isCurrentVenue(venue)"
      [attr.data-testid]="'btn-context-switcher-venue-' + venue.uuid"
      (click)="selectVenue(venue)">
      <app-default-avatar [name]="venue.name" [size]="38" />
      <span class="fb-switcher__row-text">
        <span class="fb-switcher__row-name" [appTooltipWhenTruncated]="venue.name">
          <app-highlighted-text [parts]="venue.name | highlight: normalizedQuery()" />
        </span>
        @if (showsAccountName(venue)) {
          <span class="fb-switcher__row-subtitle" [appTooltipWhenTruncated]="venue.accountName">{{
            venue.accountName
          }}</span>
        }
      </span>
      @if (isCurrentVenue(venue)) {
        <mat-icon class="fb-switcher__check" aria-hidden="true">check</mat-icon>
      }
    </button>
  }
</ng-template>

<ng-template #loadingRows let-testId>
  <div class="fb-switcher__loading" role="status" [attr.data-testid]="testId">
    @for (row of loadingPlaceholderRows; track row) {
      <span class="fb-switcher__loading-row" aria-hidden="true">
        <span class="fb-switcher__loading-avatar"></span>
        <span class="fb-switcher__loading-lines">
          <span class="fb-switcher__loading-line"></span>
          <span class="fb-switcher__loading-line fb-switcher__loading-line--short"></span>
        </span>
      </span>
    }
  </div>
</ng-template>
